import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { dashboardApi, formatCurrency, formatDate } from '@/lib/api';
import { 
  Home, FileText, TrendingUp, TrendingDown, Plus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import AppLayout from '@/components/AppLayout';
import CashflowWidget from '@/components/CashflowWidget';
import ExpenseBreakdownWidget from '@/components/ExpenseBreakdownWidget';
import UpcomingDatesWidget from '@/components/UpcomingDatesWidget';

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await dashboardApi.get();
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="w-8 h-8 border-3 border-signal-blue/30 border-t-signal-blue rounded-full animate-spin"></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-8" data-testid="dashboard-page">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-ink font-display tracking-tight">Dashboard</h1>
            <p className="mt-1 text-muted-brand">Welcome back, {user?.name?.split(' ')[0]}</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/properties">
              <Button data-testid="add-property-btn" className="bg-anchor-blue hover:bg-anchor-blue/90 rounded-xl">
                <Plus className="w-4 h-4 mr-2" />
                Add property
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-line">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-brand">Total properties</p>
                  <p className="text-3xl font-bold text-ink font-display mt-1" data-testid="total-properties">
                    {stats?.total_properties || 0}
                  </p>
                  <p className="text-xs text-muted-brand mt-1">
                    {stats?.active_properties || 0} active, {stats?.archived_properties || 0} archived
                  </p>
                </div>
                <div className="w-12 h-12 bg-signal-blue/10 rounded-xl flex items-center justify-center">
                  <Home className="w-6 h-6 text-signal-blue" strokeWidth={2} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-line">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-brand">Monthly rent</p>
                  <p className="text-3xl font-bold text-ink font-mono mt-1" data-testid="monthly-rent">
                    {formatCurrency(stats?.total_monthly_rent || 0)}
                  </p>
                  <p className="text-xs text-meadow-green mt-1 flex items-center">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    Active properties
                  </p>
                </div>
                <div className="w-12 h-12 bg-meadow-green/10 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-meadow-green" strokeWidth={2} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-line">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-brand">Monthly expenses</p>
                  <p className="text-3xl font-bold text-ink font-mono mt-1" data-testid="monthly-expenses">
                    {formatCurrency(stats?.total_monthly_expenses || 0)}
                  </p>
                  <p className="text-xs text-error mt-1 flex items-center">
                    <TrendingDown className="w-3 h-3 mr-1" />
                    All categories
                  </p>
                </div>
                <div className="w-12 h-12 bg-error/10 rounded-xl flex items-center justify-center">
                  <TrendingDown className="w-6 h-6 text-error" strokeWidth={2} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-line">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-brand">Total documents</p>
                  <p className="text-3xl font-bold text-ink font-display mt-1" data-testid="total-documents">
                    {stats?.total_documents || 0}
                  </p>
                  <p className="text-xs text-muted-brand mt-1">
                    Across all properties
                  </p>
                </div>
                <div className="w-12 h-12 bg-sunbeam-amber/10 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-sunbeam-amber" strokeWidth={2} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Cashflow Widget - Full Width */}
        <div className="mb-6">
          <CashflowWidget
            selectedPropertyIds={selectedPropertyIds}
            onPropertySelectionChange={setSelectedPropertyIds}
          />
        </div>

        {/* Bottom Grid: Expense Breakdown + Upcoming Dates + Recent Documents */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Expense Breakdown */}
          <ExpenseBreakdownWidget selectedPropertyIds={selectedPropertyIds} />

          {/* Upcoming Dates (Full CRUD) */}
          <UpcomingDatesWidget />

          {/* Recent Documents */}
          <Card className="border-line">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold font-display">Recent documents</CardTitle>
                <FileText className="w-5 h-5 text-muted-brand" />
              </div>
            </CardHeader>
            <CardContent>
              {stats?.recent_documents?.length > 0 ? (
                <div className="space-y-2" data-testid="recent-documents-list">
                  {stats.recent_documents.map((doc, index) => (
                    <div
                      key={doc.id || index}
                      className="flex items-start justify-between p-3 bg-cloud rounded-xl"
                    >
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <FileText className="w-4 h-4 text-muted-brand mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-ink truncate">
                            {doc.filename}
                          </p>
                          <p className="text-xs text-muted-brand truncate mt-0.5">
                            {doc.property_address}
                          </p>
                          <p className="text-xs text-muted-brand mt-0.5">
                            {formatDate(doc.upload_date)}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs capitalize flex-shrink-0 ml-2 border-line">
                        {doc.doc_type?.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <FileText className="w-10 h-10 text-line mb-3" />
                  <p className="text-sm text-muted-brand">No documents uploaded yet</p>
                  <p className="text-xs text-muted-brand mt-1">Upload documents to your properties</p>
                  <Link to="/properties" className="mt-3">
                    <Button variant="outline" size="sm" className="rounded-lg border-line">
                      Go to properties
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
